/* eslint-disable react-hooks/exhaustive-deps */
/** @jsx jsx */

import { useState, useEffect } from 'react'
import { jsx, Flex } from 'theme-ui'
import { Input } from '../../../../trustyle-input'
import Tooltip from '../Tooltip/tooltip'
import FormatCurrency from '../../utils/currency-formater'

interface IPropsNumberInput {
  inputName: string
  labelText?: string
  placeholderText?: string
  returnInputValue?: any
  minValue?: number
  defaultValue?: number
  maxValue?: number
  inputValuePrefix?: string
  inputValueSuffix?: string
  step?: number
  tooltipMessage?: string
  allowZero?: boolean
  decimal?: boolean
  isCurrency?: boolean
}

const NumberInput: React.FC<IPropsNumberInput> = ({
  labelText,
  inputName,
  placeholderText,
  returnInputValue,
  minValue = 0,
  defaultValue = 0,
  maxValue,
  inputValuePrefix,
  inputValueSuffix,
  step = 10,
  tooltipMessage,
  allowZero = false,
  decimal = false,
  isCurrency = false,
}): JSX.Element => {
  const [value, setValue] = useState<string>(
    inputValuePrefix
      ? `${inputValuePrefix}${defaultValue || minValue}`
      : inputValueSuffix
      ? `${defaultValue || minValue}${inputValueSuffix}`
      : `${defaultValue || minValue}`,
  )
  const [isTooltipVisible, setIsTooltipVisible] = useState<boolean>(false)

  const cleanInputValue = (value: any) => {
    let cleanInputValue = value
      .replace(inputValuePrefix, '')
      .replace(inputValueSuffix, '')
      .replace(/[^\d.-]/g, '')
      .replaceAll(' ', '')

    if (isCurrency) {
      cleanInputValue = cleanInputValue.replaceAll(',', '') // Remove delimiter
    }

    return cleanInputValue
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const clearInputValue = cleanInputValue(e.target.value)
    setValue(`${clearInputValue}`)
  }

  const removePrefixSuffix = (value: any) => {
    const clearInputValue = cleanInputValue(value)
    setValue(clearInputValue)
  }

  const IncrementValue = () => {
    let clearInputValue = cleanInputValue(value)
    const newValue = parseFloat(clearInputValue) + parseFloat(String(step) || '0')
    if ((maxValue && newValue <= maxValue) || !maxValue) {
      clearInputValue = decimal ? Math.round(newValue * 100) / 100 : newValue
    }

    setValue(`${inputValuePrefix || ''}${clearInputValue}${inputValueSuffix || ''}`)
  }

  const DecrementValue = () => {
    let clearInputValue = cleanInputValue(value)

    let newValue = parseFloat(clearInputValue) - parseFloat(String(step) || '0')

    if (newValue < minValue) newValue = minValue

    if (newValue >= minValue) {
      clearInputValue = decimal ? Math.round(newValue * 100) / 100 : newValue
    }

    setValue(`${inputValuePrefix || ''}${clearInputValue}${inputValueSuffix || ''}`)
  }

  const checkValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    const clearInputValue = cleanInputValue(e.target.value)
    let temp =
      parseFloat(clearInputValue) < 0 ? '0' : clearInputValue.trim() === '' ? '0' : clearInputValue

    if (allowZero) {
      temp === '' ? setIsTooltipVisible(true) : setIsTooltipVisible(false)
    } else {
      temp === '0' ? setIsTooltipVisible(true) : setIsTooltipVisible(false)
    }

    if (maxValue && parseFloat(temp) >= maxValue) temp = maxValue

    if (minValue && parseFloat(temp) <= minValue) temp = minValue

    if (isCurrency) temp = FormatCurrency(temp, 'GBP', '£')

    if (decimal) temp = parseFloat(temp)

    setValue(`${inputValuePrefix || ''}${temp}${inputValueSuffix || ''}`)
  }

  useEffect(() => {
    returnInputValue(
      inputName,
      inputValuePrefix
        ? value.replace(inputValuePrefix, '').replace(',', '').trim()
        : inputValueSuffix
        ? value.replace(inputValueSuffix, '').replace(',', '').trim()
        : value.replace(',', '').trim(),
    )
  }, [value])

  return (
    <div
      sx={{
        marginBottom: '20px',
      }}
    >
      <If condition={Boolean(labelText)}>
        <label htmlFor={inputName} sx={{ fontSize: [16, 18], padding: '0 0 5px 5px' }}>
          {labelText}
        </label>
      </If>
      <Flex sx={{ width: '100%', height: '46px', position: 'relative' }}>
        <Input
          sx={InputStyles}
          value={value}
          placeholder={placeholderText}
          name={inputName}
          onFocus={() => {
            removePrefixSuffix(value)
            setIsTooltipVisible(false)
          }}
          onBlur={checkValue}
          onChange={handleChange}
        />
        <If condition={Boolean(tooltipMessage)}>
          <Tooltip text={tooltipMessage} show={isTooltipVisible} />
        </If>
        <div sx={{ paddingLeft: 5 }}>
          <button type='button' sx={PlusButtonStyles} onClick={IncrementValue}>
            +
          </button>
          <button type='button' sx={MinusButtonStyles} onClick={DecrementValue}>
            -
          </button>
        </div>
      </Flex>
    </div>
  )
}

const InputStyles = {
  display: 'block',
  width: '100%',
  height: '46px',
  lineHeight: '46px',
  marginTop: -1,
  paddingLeft: '10px',
  border: 'none',
  borderRadius: '6px',
  boxSizing: 'border-box' as const,
  fontWeight: '600',
  fontSize: ['22px', '26px'],
  color: '#db4d75',
  textAlign: 'left' as const,
}

const IncrementDecrementButtonStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '34px',
  height: '21px',
  borderRadius: '6px',
  color: '#fff',
  fontWeight: '700',
  textDecoration: 'none',
  marginBottom: '4px',
  padding: '0',
  cursor: 'pointer',
  border: 'none',
}

const PlusButtonStyles = {
  ...IncrementDecrementButtonStyles,
  backgroundColor: '#db4d75',
}

const MinusButtonStyles = {
  ...IncrementDecrementButtonStyles,
  backgroundColor: '#924a8b',
}

export default NumberInput
